import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import FormContainer from '../components/FormContainer';
import Message from '../components/Message';
import { LinkContainer } from 'react-router-bootstrap';

import {
  useProfileMutation,
  useGetUserProDetailsQuery,
  useGetManagersQuery,
  useRecalculateOwnAnnualLeaveMutation,
} from '../slices/usersApiSlice';
import { setCredentials, logout } from '../slices/authSlice';
import { toast } from 'react-toastify';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice.js'; // Using your existing slice

// Import CATEGORY_OF_STAFF enum
import { CATEGORY_OF_STAFF } from '../utils/enums';

import { apiSlice } from '../slices/apiSlice'; 

//#f5f5dc
const dropdownStyle = {
  backgroundColor: '#F5F5F5', //'#F0E68C'                 //'lightyellow',
  // Add other styles as needed
};
const textStyle = {
  backgroundColor: '#f5f5dc',
  // Add other styles as needed
};

// Same hours-as-hh:mm formatting used on the admin Annual Leave calculator
// screens (AnnualLeaveCalcScreen.jsx's fmtHrs) — e.g. 5.52 hrs -> "5:31",
// so a staff member's balance reads the same way the admin's AL Breakdown
// dialog does, rather than as a decimal.
const fmtHrsAsHM = (h) => {
  if (h === null || h === undefined || h === '' || Number.isNaN(Number(h))) return '—';
  const totalMinutes = Math.round(Number(h) * 60);
  const sign = totalMinutes < 0 ? '-' : '';
  const abs = Math.abs(totalMinutes);
  const hrs = Math.floor(abs / 60);
  const mins = abs % 60;
  return `${sign}${hrs}:${String(mins).padStart(2, '0')}`;
};

const ProfileScreen = () => {
  
  const [name, setName] = useState('-');
  const [email, setEmail] = useState('');
  const [isLocum, setIsLocum] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');

  const [employeenumber, setEmployeenumber] = useState('');
  const [jobtitle, setJobtitle] = useState('');
  
  // Updated fields for hospital location
  const [speciality, setSpeciality] = useState(''); // This will be wardDepartmentArea
  const [specialityID, setSpecialityID] = useState(''); // This will be hospitalLocation _id
 
  const [directoratedepartment, setDirectoratedepartment] = useState(''); // This will be directorate
  const [directorateID, setDirectorateID] = useState(''); // This will be hospitalLocation _id (same as specialityID)
  
  const [comment, setComment] = useState('--');
  const [effectiveDate, setEffectiveDate] = useState(new Date());
  const [reviewDate, setReviewDate] = useState(new Date());

  const [salutation, setSalutation] = useState('--');

  // ── Ward scope (same rules as RegisterScreen) ──────────────────────────
  // onlyOwnWard=true  → wardAreaInclusions is the list of EXTRA wards (by
  //   HospitalLocation _id) this person can also work, on top of their own.
  // onlyOwnWard=false → wardExclusions is the list of ward NAMES this
  //   person should never be assigned to; everywhere else is fine.
  // Only one of the two lists applies at a time, matching the flag.
  const [onlyOwnWard,        setOnlyOwnWard]        = useState(false);
  const [wardAreaInclusions, setWardAreaInclusions] = useState([]);
  const [wardExclusions,     setWardExclusions]     = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [updateProfile, { isLoading: loadingUpdateProfile }] =
    useProfileMutation();

  const [recalculateAnnualLeave, { isLoading: recalculatingAnnualLeave }] =
    useRecalculateOwnAnnualLeaveMutation();

  // Use your existing hospital locations query
  const {
    data: hospitalLocations,
    isLoading: isLoadingHL,
    error: errorHL,
    refetch: refetchHL,
  } = useGetHospitalLocationsQuery();

  const { userInfo } = useSelector((state) => state.auth);

  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const redirect = sp.get('redirect') || '/';

  const {
    data: managers,
    refetch: refetchM,
    isLoading: isLoadingM,
    errorM,
  } = useGetManagersQuery();

  const {
    data: user,
    isLoading: isLoadingU,
    error: errorU,
    refetch: refetchU,
  } = useGetUserProDetailsQuery(userInfo._Id);

  // Define the options data (keeping this as is)
  const contractData = {
    'Public Only Consultant Contract': [
      'POCC Clinician',
      'POCC Academic Professor',
      'POCC Associate Professor',
      'POCC Senior Lecturer',
    ],
    '2008 Contract': [
      'Type A',
      'Type B',
      'Type B Academic',
      'Type B*',
      'Type B* Academic',
      'Type C',
      'Type C Academic',
    ],
    'Buckley Contract': [
      'Category I',
      'Category II',
      'Category II Senior Lecturer',
    ],
  };

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setIsLocum(user.isLocum);
      setFirstname(user.firstname);
      setLastname(user.lastname);

      setEmployeenumber(user.employeenumber);
      setJobtitle(user.jobtitle);
      
      // These now come from hospital location
      setSpeciality(user.speciality); // wardDepartmentArea
      setSpecialityID(user.specialityID); // hospitalLocation _id
      
      setDirectoratedepartment(user.directoratedepartment); // directorate
      setDirectorateID(user.directorateID); // hospitalLocation _id
      
      setComment(user.comment);
      setSalutation(user.salutation);

      setOnlyOwnWard(!!user.onlyOwnWard);
      // wardAreaInclusions comes back populated as HospitalLocation objects
      // ({_id, wardDepartmentArea, ...}) — the multi-select's option values
      // are plain _id strings, so normalize either shape to just the id.
      setWardAreaInclusions(
        (user.wardAreaInclusions || []).map((loc) => (typeof loc === 'string' ? loc : loc._id))
      );
      setWardExclusions(user.wardExclusions || []);

      const reviewDate1 = new Date(user.reviewDate);
      let reviewDate2 = '';

      if (
        reviewDate1 &&
        reviewDate1 instanceof Date &&
        !isNaN(reviewDate1.getTime())
      ) {
        reviewDate2 = reviewDate1.toISOString().slice(0, 10);
      } else {
        reviewDate2 = '';
      }

      const effectiveDate1 = new Date(user.effectiveDate);
      let effectiveDate2 = '';

      if (
        effectiveDate1 &&
        effectiveDate1 instanceof Date &&
        !isNaN(effectiveDate1.getTime())
      ) {
        effectiveDate2 = effectiveDate1.toISOString().slice(0, 10);
      } else {
        effectiveDate2 = '';
      }

      setReviewDate(reviewDate2);
      setEffectiveDate(effectiveDate2);
    }
  }, [user]);

  // If the person's own ward changes, drop it from either list — it doesn't
  // make sense as an "extra" or "excluded" ward once it IS their own ward.
  useEffect(() => {
    setWardAreaInclusions((prev) => prev.filter((id) => id !== specialityID));
    setWardExclusions((prev) => prev.filter((name) => name !== speciality));
  }, [specialityID, speciality]);

  // Ward scope only applies to staff who cover more than one ward — the
  // three Bank/ECT categories always work their own assigned ward only, so
  // the whole section is hidden (and reset) for them. Same rules as
  // RegisterScreen.
  const WARD_SCOPE_EXCLUDED_CATEGORIES = ['Bank-HCA', 'Bank-RGN', 'ECT-HCA'];
  // Also requires an own ward to already be selected — "own ward only" and
  // "additional to my own ward" are meaningless before that exists.
  const showWardScope = !WARD_SCOPE_EXCLUDED_CATEGORIES.includes(jobtitle) && !!specialityID;

  useEffect(() => {
    if (!showWardScope) {
      setOnlyOwnWard(false);
      setWardAreaInclusions([]);
      setWardExclusions([]);
    }
  }, [showWardScope]);

  //__________________________________________________________________

  // Self-service Annual Leave recalculation — re-runs whichever calculator
  // (Bank or Non-Bank) covers this staff member's own category from that
  // calculator's configured start date through today, and writes the
  // result to Opening Balance server-side. Brought Forward is never
  // touched. Refetches the profile afterwards so the displayed total
  // picks up the new figure straight away.
  // Mirrors the backend's once-per-day guard (same calendar day, server
  // date) so the button is disabled proactively rather than the person
  // only finding out after clicking it and getting the 429 error back.
  const alreadyRecalculatedToday = (() => {
    if (!user?.lastAnnualLeaveRecalculatedAt) return false;
    const lastRunKey = new Date(user.lastAnnualLeaveRecalculatedAt).toISOString().slice(0, 10);
    const todayKey = new Date().toISOString().slice(0, 10);
    return lastRunKey === todayKey;
  })();

  const handleRecalculateAnnualLeave = async () => {
    try {
      const result = await recalculateAnnualLeave().unwrap();
      await refetchU();
      toast.success(`Annual Leave recalculated — new balance: ${fmtHrsAsHM(result.trueBalance)} hrs`);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to recalculate Annual Leave');
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
    } else {
      toast.success('Updating profile ');

      try {
        const res = await updateProfile({
          _id: userInfo._id,
          name,
          email,
          isLocum,
          password,
          employeenumber,
          firstname,
          lastname,
          jobtitle,
          speciality, // wardDepartmentArea
          specialityID, // hospitalLocation _id
          directoratedepartment, // directorate
          directorateID, // hospitalLocation _id (same as specialityID)
          effectiveDate,
          comment,
          salutation,
          onlyOwnWard,
          wardAreaInclusions,
          wardExclusions,
        }).unwrap();

        /*
        dispatch(setCredentials({ ...res }));
        refetchU();
        toast.success('Profile updated successfully');
        */

        // Log out the user after successful registration
        dispatch(logout());
        dispatch(apiSlice.util.resetApiState()); 
        toast.success('Profile updated successfully');
        
        // Navigate to login page
        navigate('/login?message=registration-pending');


      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Group hospital locations by directorate for better organization
  const getLocationsByDirectorate = () => {
    if (!hospitalLocations) return {};
    
    return hospitalLocations.reduce((acc, location) => {
      if (!acc[location.directorate]) {
        acc[location.directorate] = [];
      }
      acc[location.directorate].push(location);
      return acc;
    }, {});
  };

  const locationsByDirectorate = getLocationsByDirectorate();
  const directorates = Object.keys(locationsByDirectorate).sort();

  // Find the current location details if specialityID exists
  const currentLocation = hospitalLocations?.find(loc => loc._id === specialityID);

  //_____________________________________________________________________________________________________

  return (
    <Container fluid>
      <h2>My Profile...</h2>

      {isLoadingU || isLoadingHL || isLoadingM ? (
        <Loader />
      ) : errorHL ? (
        <Message variant='danger'>
          {errorHL?.data?.message || errorHL?.error || 'Error loading hospital locations'}
        </Message>
      ) : errorM ? (
        <Message variant='danger'>
          {errorM.data.message || errorM.error}
        </Message>
      ) : (
        <Form onSubmit={submitHandler}>
          <Row>
            {/* First Column */}
            <Col md={6}>
              <div
                style={{
                  border: '1px solid #ddd',
                  padding: '10px',
                  borderRadius: '10px',
                  backgroundColor: '#F0F8FF',
                }}
              >
                <Row>
                  <Col sm={3}>
                    <Form.Group controlId='salutation'>
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        as='select'
                        value={salutation}
                        onChange={(e) => setSalutation(e.target.value)}
                        style={textStyle}
                      >
                        <option value=''>Select Title</option>
                        <option value='Cllr.'>Cllr.</option>
                        <option value='Mr.'>Mr.</option>
                        <option value='Ms.'>Ms.</option>
                        <option value='Mrs'>Mrs</option>
                        <option value='Miss'>Miss</option>
                        <option value='Dr.'>Dr.</option>
                        <option value='Professor'>Professor</option>
                        <option value='Mx'>Mx</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col sm={4}>
                    <Form.Group controlId='firstname'>
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type='text'
                        placeholder='Enter First Name'
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        style={textStyle}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={5}>
                    <Form.Group controlId='lastname'>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type='text'
                        placeholder='Enter Last Name'
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        style={textStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group controlId='email'>
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type='email'
                    placeholder='Enter Email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={textStyle}
                  />
                </Form.Group>

                <Row>
                  <Col sm={6}>
                    <Form.Group controlId='employeenumber'>
                      <Form.Label>Employee Number</Form.Label>
                      <Form.Control
                        type='text'
                        placeholder='Enter Employee Number'
                        value={employeenumber}
                        onChange={(e) => setEmployeenumber(e.target.value)}
                        style={textStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group controlId='jobtitle'>
                      <Form.Label>Job Title / Category of Staff</Form.Label>
                      <Form.Control
                        as='select'
                        value={jobtitle}
                        onChange={(e) => setJobtitle(e.target.value)}
                        style={textStyle}
                      >
                        <option value=''>Select Job Title</option>
                        {CATEGORY_OF_STAFF.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/*
                    <Col sm={6}>
                      <Form.Group controlId="effectiveDate">
                        <Form.Label>Effective Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={effectiveDate}
                          readOnly
                          style={{ ...textStyle, cursor: 'not-allowed', opacity: 0.75 }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                          Set by administrator after approval
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group controlId="reviewDate">
                        <Form.Label>Review Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={reviewDate}
                          readOnly
                          style={{ ...textStyle, cursor: 'not-allowed', opacity: 0.75 }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                          Set to 1 year from today by default
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    */}
                    
                </Row>
              </div>

              <div>
                <p></p>
              </div>

              <div
                style={{
                  border: '1px solid #ddd',
                  padding: '10px',
                  borderRadius: '10px',
                  backgroundColor: '#F0F8FF',
                }}
              >
                <Form.Group className='my-2' controlId='password'>
                  <Form.Label>Change Password</Form.Label>
                  <Form.Control
                    type='password'
                    placeholder='Set password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={textStyle}
                  ></Form.Control>
                </Form.Group>

                <Form.Group className='my-2' controlId='confirmPassword'>
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type='password'
                    placeholder='Confirm password'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={textStyle}
                  ></Form.Control>
                </Form.Group>
              </div>
            </Col>

            {/* Second Column - Hospital Location Selection */}
            <Col md={6}>
              <div
                style={{
                  border: '1px solid #ddd',
                  padding: '10px',
                  borderRadius: '10px',
                  backgroundColor: '#F0F8FF',
                }}
              >
                {/* Directorate Selection */}
                <Form.Group controlId='setDirectorate'>
                  <Form.Label>Directorate</Form.Label>
                  <Form.Control
                    as='select'
                    value={directoratedepartment}
                    onChange={(e) => {
                      const selectedDirectorate = e.target.value;
                      setDirectoratedepartment(selectedDirectorate);
                      
                      // Clear the ward selection when directorate changes
                      setSpeciality('');
                      setSpecialityID('');
                      setDirectorateID('');
                    }}
                    style={textStyle}
                  >
                    <option value=''>Select Directorate</option>
                    {directorates.map((directorate) => (
                      <option key={directorate} value={directorate}>
                        {directorate}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                {/* Ward/Department Area Selection (previously Speciality) */}
                <Form.Group controlId='wardDepartmentArea'>
                  <Form.Label>Ward / Department Area</Form.Label>
                  <Form.Control
                    as='select'
                    value={speciality}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const selectedIndex = e.target.selectedIndex;
                      const selectedID = e.target.options[selectedIndex].getAttribute('data-id');
                      const selectedDirectorate = e.target.options[selectedIndex].getAttribute('data-directorate');
                      const selectedDepartment = e.target.options[selectedIndex].getAttribute('data-department');
                      
                      setSpeciality(selectedValue);
                      setSpecialityID(selectedID);
                      setDirectorateID(selectedID); // Using the same ID for both
                      
                      // Also update the directorate based on the selected ward
                      if (selectedDirectorate) {
                        setDirectoratedepartment(selectedDirectorate);
                      }
                    }}
                    disabled={!directoratedepartment && !specialityID}
                    style={textStyle}
                  >
                    <option value=''>
                      {directoratedepartment 
                        ? 'Select Ward/Department Area' 
                        : 'Please select a directorate first'}
                    </option>
                    {directoratedepartment && locationsByDirectorate[directoratedepartment]?.map((location) => (
                      <option
                        key={location._id}
                        value={location.wardDepartmentArea}
                        data-id={location._id}
                        data-directorate={location.directorate}
                        data-department={location.department}
                      >
                        {location.wardDepartmentArea} 
                        {location.department && ` - ${location.department}`}
                        {location.costCentreCode && ` (${location.costCentreCode})`}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                {/* Display current selection info */}
                {currentLocation && (
                  <div className="mt-2 p-2 bg-light rounded">
                    <small>
                      <strong>Selected Location Details:</strong><br />
                      Cost Centre: {currentLocation.costCentreCode}<br />
                      Department: {currentLocation.department}
                    </small>
                  </div>
                )}

                {/* ── Ward scope (not applicable to Bank-HCA / Bank-RGN / ECT-HCA) ── */}
                {showWardScope && (
                  <div style={{
                    marginTop: 16, padding: 14, borderRadius: 10,
                    backgroundColor: '#fff', border: '1px solid #cfe3f7',
                  }}>
                    <Form.Group controlId="onlyOwnWard">
                      <Form.Check
                        type="checkbox"
                        label="Own Ward Only — I should only be assigned shifts in my own ward (plus any wards I choose to include below)"
                        checked={onlyOwnWard}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setOnlyOwnWard(checked);
                          // Only one list is ever meaningful at a time — clear the other.
                          if (checked) setWardExclusions([]);
                          else setWardAreaInclusions([]);
                        }}
                      />
                    </Form.Group>

                    {onlyOwnWard ? (
                      <Form.Group controlId="wardAreaInclusions" className="mt-2">
                        <Form.Label>Additional Wards You Can Also Work (optional)</Form.Label>
                        <Form.Control
                          as="select"
                          multiple
                          value={wardAreaInclusions}
                          onChange={(e) =>
                            setWardAreaInclusions(Array.from(e.target.selectedOptions, (o) => o.value))
                          }
                          style={textStyle}
                        >
                          {[...(hospitalLocations ?? [])]
                            .filter((loc) => loc._id !== specialityID)
                            .sort((a, b) => (a.wardDepartmentArea || '').localeCompare(b.wardDepartmentArea || ''))
                            .map((loc) => (
                              <option key={loc._id} value={loc._id}>
                                {loc.wardDepartmentArea}
                                {loc.department && ` - ${loc.department}`}
                              </option>
                            ))}
                        </Form.Control>
                        <Form.Text className="text-muted">
                          Hold Ctrl (Windows) or Cmd (Mac) to select more than one. You'll only be
                          assigned shifts in your own ward or the wards selected here.
                        </Form.Text>
                      </Form.Group>
                    ) : (
                      <Form.Group controlId="wardExclusions" className="mt-2">
                        <Form.Label>Wards to Exclude (optional)</Form.Label>
                        <Form.Control
                          as="select"
                          multiple
                          value={wardExclusions}
                          onChange={(e) =>
                            setWardExclusions(Array.from(e.target.selectedOptions, (o) => o.value))
                          }
                          style={textStyle}
                        >
                          {[...new Set((hospitalLocations ?? []).map((loc) => loc.wardDepartmentArea))]
                            .filter((name) => name && name !== speciality)
                            .sort((a, b) => a.localeCompare(b))
                            .map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                        </Form.Control>
                        <Form.Text className="text-muted">
                          Hold Ctrl (Windows) or Cmd (Mac) to select more than one. You'll be able to
                          see and be assigned shifts in any ward except the ones selected here.
                        </Form.Text>
                      </Form.Group>
                    )}
                  </div>
                )}

                <div
                  className="my-3 p-3"
                  style={{ border: '1px solid #b3d1e6', borderRadius: 6, backgroundColor: '#f5faff' }}
                >
                  <strong>Annual Leave Balance:</strong>{' '}
                  {fmtHrsAsHM(user?.annualLeaveAvailable ?? user?.openingBalance ?? 0)} hrs
                  <div className="mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-primary"
                      onClick={handleRecalculateAnnualLeave}
                      disabled={recalculatingAnnualLeave || alreadyRecalculatedToday}
                    >
                      {recalculatingAnnualLeave
                        ? 'Recalculating...'
                        : alreadyRecalculatedToday
                        ? 'Already recalculated today'
                        : 'Recalculate'}
                    </Button>
                  </div>
                  <Form.Text className="text-muted d-block mt-1">
                    Your current Annual Leave available (your Opening Balance, plus Brought Forward
                    where applicable to your staff category). Recalculating re-runs your Annual
                    Leave calculation from this year's configured start date through today and
                    updates your Opening Balance — it does not change Brought Forward itself.
                    Limited to once per day.
                  </Form.Text>
                </div>

                <Form.Group className='my-2' controlId='comment'>
                  <Form.Label>Comments</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={3}
                    placeholder='Comments'
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    style={textStyle}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>

          <Button type='submit' variant='primary'>
            Update
          </Button>
        </Form>
      )}
    </Container>
  );
};

export default ProfileScreen;
